import React, { CSSProperties } from 'react';
import dayjs from 'dayjs';
import { Box } from 'theme-ui';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { getDateSelectCalendarStyles } from './style';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CalendarPicker, CalendarPickerProps } from '@mui/x-date-pickers';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Theme from 'Common/themePeazy.json';

dayjs.extend(LocalizedFormat);

const now = dayjs();

export type DateSelectProps = {
  label?: string;
  help?: string;
  onSelect: (v: number) => void;
  value?: number;
  slots: number[];
  availability: string;
  onMonthChange?: (date: dayjs.Dayjs) => void;
  monthUnix: number | undefined;
  styleConfig?: {
    cellHeight?: string | string[];
    cellFontSize?: string | string[];
    containerMarginTop?: string | string[],
    containerMarginBottom?: string | string[],
    containerMarginX?: string | string[],
    containerWidth?: string | string[],
  };
  disabled?: boolean;
  calendarClasses?: string;
}

const theme = createTheme({
  typography: {
    caption: {
      color: Theme.colors.darkBlue,
      fontWeight: '600',
      lineHeight: '17px',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    }
  },
  palette: {
    primary: { main: Theme.colors.darkBlue, dark: Theme.colors.darkBlue }
  },
});
(theme.breakpoints as any).map = Array.prototype.map;

const DateSelect: React.FC<DateSelectProps> = ({
  label,
  onSelect,
  value,
  slots,
  onMonthChange,
  monthUnix,
  styleConfig,
  disabled,
  calendarClasses = '',
}) => {
  const getFormattedDay = (day: string) => {
    switch (day) {
      case 'Su':
        return 'SUN';
      case 'Mo':
        return 'MON';
      case 'Tu':
        return 'TUE';
      case 'We':
        return 'WED';
      case 'Th':
        return 'THU';
      case 'Fr':
        return 'FRI';
      case 'Sa':
        return 'SAT';
      default:
        return '';
    }
  };
  const calendarDateProps: CalendarPickerProps<dayjs.Dayjs> = {
    reduceAnimations: true,
    onChange: value => {
      if (value === null) {
        return;
      }

      const v = typeof value === 'object' ?
        value.unix() :
        dayjs.unix(value).unix();
      if (v) {
        onSelect && onSelect(v);
      }
    },
    disablePast: true,
    shouldDisableDate: (d: dayjs.Dayjs) => {
      return disabled || !slots.some(
        s => dayjs.unix(s).isSame(d, 'd')
      );
    },
    dayOfWeekFormatter: (day) => getFormattedDay(day),
    date: (() => {
      if (value) {
        const djsv = dayjs.unix(value).hour(0).minute(0).second(0).millisecond(0);
        if (monthUnix) {
          const monthDjs = dayjs.unix(monthUnix);
          if (djsv.isSame(monthDjs, 'M')) {
            return djsv;
          } else {
            return null;
          }
        }

        return djsv;
      }
      return null;
    })(),
    defaultCalendarMonth: value ?
      dayjs.unix(value).hour(0).minute(0).second(0).millisecond(0) : undefined,
    openTo: 'day',
    views: ['day'],
    maxDate: now.clone().startOf('d').add(1, 'y'),
    minDate: now.clone().startOf('d'),
    onMonthChange,
  };

  return (
    <div className="flex flex-col items-start w-full">
      {label && (
        <span className="heading">
          {label}
        </span>
      )}
      <div className="flex w-full">
        <Box sx={{
          ...getDateSelectCalendarStyles(
            styleConfig?.cellHeight,
            styleConfig?.cellFontSize,
            styleConfig?.containerMarginTop,
            styleConfig?.containerMarginBottom,
            styleConfig?.containerMarginX,
            styleConfig?.containerWidth,
          ),
          width: '100%',
        }}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CalendarPicker
                showDaysOutsideCurrentMonth={false}
                className={`w-full sm:text-xs lg:text-sm max-h-none ${calendarClasses}`}
                {...calendarDateProps}
              />
            </LocalizationProvider>
          </ThemeProvider>
        </Box>
      </div>
    </div>
  );
};

export default DateSelect;
